import type { ActivityEvent } from "@/lib/types";
import { readCase, writeCase } from "./store";

/**
 * Newest first — the timeline is read from the top.
 *
 * Exported because the case context seeds its state straight from the stored
 * record and has to apply the same ordering; an audit trail that reads
 * oldest-first on load and newest-first after an edit is a bug waiting to be
 * reported.
 */
export function sortActivity(events: ActivityEvent[]): ActivityEvent[] {
  return [...events].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

export async function listActivity(caseId: string): Promise<ActivityEvent[]> {
  return sortActivity((await readCase(caseId))?.activity ?? []);
}

let sequence = 0;

const MINUTE = 60_000;

/**
 * When a new event happened.
 *
 * The fixtures are dated at fixed points in the product's fictional working day
 * — ACME's last seeded event is at 14:31. Wall-clock time is unrelated to that,
 * so a new event stamped with `Date.now()` can land before events already in
 * the trail and sort underneath them. An audit trail whose newest entry is not
 * at the top is worse than useless, so a new event is always placed after the
 * last one. Once the store is a real backend, seeded timestamps are genuinely
 * in the past and this clamp never binds.
 */
function nextTimestamp(existing: ActivityEvent[]): string {
  const now = Date.now();
  const latest = existing.reduce((max, event) => Math.max(max, Date.parse(event.at)), 0);
  return new Date(Math.max(now, latest + MINUTE)).toISOString();
}

/**
 * Appends to the audit trail. `at` is stamped here rather than passed in so a
 * caller cannot backdate an event.
 */
export async function appendActivity(
  caseId: string,
  event: Omit<ActivityEvent, "id" | "at">,
): Promise<ActivityEvent[]> {
  sequence += 1;
  await writeCase(caseId, (record) => {
    record.activity.push({
      ...event,
      id: `act-live-${sequence}`,
      at: nextTimestamp(record.activity),
    });
  });
  return listActivity(caseId);
}
